import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  useTheme,
  Tabs,
  Tab,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ClientInformation from "./steps/BookingClientInformation";
import ServiceInformation from "./steps/BookingServiceDetails";
import PackageSelection from "./steps/AddBookingPackageSelection";
import SiteDetails from "./steps/SiteDetails";
import { tokens } from "../../theme";
import apiService from "../../service/Api";
import type { BookingData } from "../../types/bookings";
import type { StepConfig } from "../../types/steps";
import { initialValues } from "./AddBooking";
import type { BookingResponse } from "./types/edit-booking";
import { toast } from "react-toastify";
import type {
  EventListPayload,
  ServicesListPayload,
} from "./types/add-booking";
import type { IContactFormData } from "../../types/contact";
import ConferenceBookingForm from "./components/ConferenceBookingForm";

const buildBookingFormData = async (
  values: BookingData,
  isUpdate = false,
  bookingType = "FUNERAL"
): Promise<FormData> => {
  const formData = new FormData();

  const append = (key: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== "") {
      if (typeof value === "boolean" || typeof value === "number") {
        formData.append(key, value.toString());
      } else if (typeof value === "string") {
        formData.append(key, value);
      } else if (value instanceof Blob) {
        formData.append(key, value);
      } else {
        formData.append(key, JSON.stringify(value));
      }
    }
  };

  // Additional Services
  const additionalServiceCategories = [
    "AV Equipment",
    "Audio Visual Equipment",
    "Design",
    "Other",
    "Screens",
    "Video & Audio",
    "Visual Presentation & Audio",
  ];
  const extractedServiceIds: string[] = [];
  additionalServiceCategories.forEach((cat) => {
    const catVals = (values as Record<string, unknown>)[cat];
    if (Array.isArray(catVals)) extractedServiceIds.push(...catVals);
  });

  const uniqueServiceIds = Array.from(
    new Set(extractedServiceIds.filter(Boolean))
  );

  append("booking_type", values.booking_type?.toUpperCase());
  append("total", values.total?.toFixed(2));
  append("payment_method", values.payment_method || "pending");
  append("payment_status", values.payment_status || "pending");
  append("status", values.status || "pending");
  append("account_status", values.account_status || "active");
  append("contact_id", values.contact_id);
  append("company_contact_id", values.company_contact_id);
  append("email", values.email);
  append("salutation", values.client_salutation);
  append("phone", values.phone);
  append("first_name", values.first_name);
  append("surname", values.surname);
  append("address", values.address);
  append("city", values.city);
  append("country", values.country);
  append("customer_address", values.customer_address);
  append("address_one", values.address_one);
  append("address_two", values.address_two);
  append("suburb", values.suburb);
  append("relationship_to_deceased", values.relationship_to_deceased);
  append("source_of_booking", values.source_of_booking);
  append("company_name", values.company_name);
  append("branch_name", values.branch_name);
  append("branch_rep", values.branch_rep);
  append("church_name", values.church_name);
  append("title", values.title);
  append("service_notes", values.service_notes);
  append("special_request", values.special_request);
  if (values.package?.id) {
    append("package_item[id]", values.package_item?.id);
    append("package_item[name]", values.package.name);
    append(
      "package_item[description]",
      values.package.contents?.[0] || "Package"
    );
    append("package_item[package]", values.package.id);
    append("package_item[price]", values.package.price?.toFixed(2));

    uniqueServiceIds.forEach((id) => {
      append("package_item[services][]", id);
    });
  }

  if (values.deceased_list?.length) {
    values.deceased_list.forEach((d, index) => {
      const prefix = `deceased_list[${index}]`;
      append(`${prefix}[first_name]`, d.deceased_name);
      append(`${prefix}[surname]`, d.deceased_surname);
      append(`${prefix}[second_name]`, d.second_name);
      append(`${prefix}[date_of_birth]`, d.date_of_birth);
      append(`${prefix}[date_of_death]`, d.date_of_death);
      append(`${prefix}[gender]`, d.gender);
      append(`${prefix}[salutation]`, d.deceased_salutation);
      append(`${prefix}[nickname]`, d.nickname);
      append(`${prefix}[relationship_to_deceased]`, d.relationship_to_deceased);
      append(`${prefix}[religion]`, d.religion);
      append(`${prefix}[denomination]`, d.denomination);

      if (d.deceasedImageFile instanceof File) {
        formData.append(
          `${prefix}[person_image]`,
          d.deceasedImageFile,
          d.deceasedImageFile.name
        );
      } else if (isUpdate && d.person_image) {
        append(`${prefix}[person_image]`, d.person_image);
      }
    });
  }

  if (values.services_list?.length) {
    if (bookingType !== "CONFERENCE" && bookingType !== "OTHER") {
      values.services_list.forEach(
        (service: ServicesListPayload, index: number) => {
          const prefix = `services_list[${index}]`;
          Object.entries(service).forEach(([key, value]) => {
            if (key === "start_time" || key === "end_time") {
              append(`${prefix}[${key}]`, value ? `${value}:00` : null);
            } else if (key === "venue") {
              return;
            } else {
              append(`${prefix}[${key}]`, value);
            }
          });
        }
      );
    }
  } else if (values.venue_ids?.length) {
    values.venue_ids.forEach((venueId, index) => {
      const prefix = `services_list[${index}]`;
      append(`${prefix}[mc_name]`, values.mc_name);
      append(`${prefix}[eulogist_name]`, values.eulogist_name);
      append(`${prefix}[date_of_service]`, values.date_of_service);
      append(
        `${prefix}[start_time]`,
        values.start_time ? `${values.start_time}:00` : null
      );
      append(
        `${prefix}[end_time]`,
        values.end_time ? `${values.end_time}:00` : null
      );
      append(`${prefix}[venue_id]`, Number.parseInt(venueId));
      append(`${prefix}[church_name]`, values.church_name);
      append(`${prefix}[leader_preacher_name]`, values.leader_preacher_name);
      append(
        `${prefix}[leader_preacher_surname]`,
        values.leader_preacher_surname
      );
      append(`${prefix}[preacher_name]`, values.preacher_name);
      append(`${prefix}[preacher_surname]`, values.preacher_surname);
      append(`${prefix}[service_address]`, values.service_address);
      append(`${prefix}[service_location]`, values.service_location);
      append(`${prefix}[type_of_service]`, values.type_of_service);
      append(`${prefix}[service_notes]`, values.service_notes);
      append(`${prefix}[special_request]`, values.special_request);
    });
  }

  if (values.events_list?.length) {
    values.events_list.forEach((event: EventListPayload, index: number) => {
      const prefix = `events_list[${index}]`;
      append(`${prefix}[title]`, event.title);
      append(`${prefix}[hosts]`, event.hosts);
      append(`${prefix}[guests]`, event.guests);
      append(`${prefix}[host_surname]`, event.host_surname);
      append(`${prefix}[venue_id]`, event.venue_id);
      append(`${prefix}[start_date_of_event]`, event.start_date_of_event);
      append(`${prefix}[end_date_of_event]`, event.end_date_of_event);
      append(`${prefix}[start_time]`, event.start_time);
      append(`${prefix}[end_time]`, event.end_time);
      append(`${prefix}[enable_livestreaming]`, event.enable_livestreaming);
      append(`${prefix}[expected_audience_size]`, event.expected_audience_size);
      append(`${prefix}[record_stream]`, event.record_stream);
      append(`${prefix}[streaming_platform]`, event.streaming_platform);
      append(`${prefix}[session_to_stream]`, event.session_to_stream);
      append(`${prefix}[specific_sessions]`, event.specific_sessions);
      append(`${prefix}[access_type]`, event.access_type);
      append(
        `${prefix}[provide_on_demand_access]`,
        event.provide_on_demand_access
      );
      append(`${prefix}[service_notes]`, event.service_notes);
      append(`${prefix}[special_request]`, event.special_request);
    });
  }

  return formData;
};

const EditBooking = () => {
  const [loading, setLoading] = useState(true);
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState(0);
  const [currentServiceType, setCurrentServiceType] = useState("FUNERAL");
  const [bookingData, setBookingData] = useState<BookingData>(initialValues);
  const [submitting, setSubmitting] = useState(false);
  const [, setSubmissionError] = useState<string | null>(null);
  const [, setSubmissionSuccess] = useState<boolean | null>(null);
  const [, setModalOpen] = useState(false);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [, setModalBookingTitle] = useState("");
  const navigate = useNavigate();
  const [currentContact, setCurrentContact] = useState<IContactFormData | null>(
    null
  );

  useEffect(() => {
    const fetchBookingData = async () => {
      if (!id) return;

      try {
        const res = await apiService.get<BookingResponse>(
          `/api/bookings/booking/${id}/`
        );
        const data = res.data;

        const isConferenceOrEvent =
          data.booking_type === "CONFERENCE" || data.booking_type === "OTHER";
        const eventOrServiceData = isConferenceOrEvent
          ? data.events?.[0]
          : data.services?.[0];
        const venueData = eventOrServiceData?.venue;

        const currentContactData: IContactFormData = {
          id: data.contact?.id || 0,
          name: `${data?.contact?.name || ""}`.trim(),
          email: data.contact?.email || null,
          phone: data.contact?.phone || null,
          contact_type: "individual",
          first_name: data.contact?.first_name || "",
          last_name: data.contact?.last_name || "",
          created_at: data.created_at || new Date().toISOString(),
          created_by: null,
          company_id: data.contact?.company_id || null,
          facebook_profile: null,
          instagram_profile: null,
          twitter_profile: null,
          linkedin_profile: null,
          contact_image: data.contact?.contact_image || null,
        };

        setCurrentContact(currentContactData);

        setCurrentServiceType(data.booking_type);

        const servicesList =
          (data.services || []).map((service) => ({
            id: service.id,
            mc_name: service.mc_name || null,
            eulogist_name: service.eulogist_name || null,
            date_of_service: service.date_of_service || null,
            start_time: service.start_time?.slice(0, 5) || null,
            end_time: service.end_time?.slice(0, 5) || null,
            venue_id: service.venue?.id ?? null,
            church_name: service.church_name || null,
            leader_preacher_name: service.leader_preacher_name || null,
            leader_preacher_surname: service.leader_preacher_surname || null,
            preacher_name: service.preacher_name || null,
            preacher_surname: service.preacher_surname || null,
            type_of_service: service.type_of_service || null,
            service_notes: service.service_notes || null,
            special_request: service.special_request || null,
            service_address: null,
            service_location: null,
          })) || [];

        const eventsList =
          (data.events || []).map((event) => ({
            id: event.id,
            title: event.title || null,
            hosts: event.hosts || null,
            host_surname: event.host_surname || null,
            guests: event.guests || null,
            guest_surname: event.guest_surname || null,
            venue_id: event.venue?.id ?? null,
            service_notes: event.service_notes || null,
            special_request: event.special_request || null,
            conference_name: event.conference_name || null,
            start_date_of_event: event.start_date_of_event || null,
            end_date_of_event: event.end_date_of_event || null,
            start_time: event.start_time?.slice(0, 5) || null,
            end_time: event.end_time?.slice(0, 5) || null,
            expected_audience_size: event.expected_audience_size ?? null,
            access_type: event.access_type || null,
            enable_livestreaming: event.enable_livestreaming ?? null,
            streaming_platform: event.streaming_platform || null,
            session_to_stream: event.session_to_stream || null,
            specific_sessions: event.specific_sessions || null,
            record_stream: event.record_stream ?? null,
            provide_on_demand_access: event.provide_on_demand_access ?? null,
            retention_duration: event.retention_duration || null,
            require_custom_branding: event.require_custom_branding ?? null,
            branding_requirements: event.branding_requirements || null,
          })) || [];

        setBookingData({
          ...initialValues,
          booking_type: data.booking_type,
          status: data.status,
          payment_method: data.payment_method,
          total: Number.parseFloat(data.total || "0"),
          service_type: data.service || "",
          source_of_booking: data.source_of_booking || "",
          contact_id: data.contact?.id,
          first_name: data.contact?.first_name || "",
          surname: data.contact?.last_name || "",
          email: data.contact?.email || "",
          phone: data.contact?.phone || "",
          client_salutation: data.contact?.salutation || "",
          special_request: data.services?.[0]?.special_request || "",
          notes: data.services?.[0]?.service_notes || "",
          title: data.events?.[0]?.title || "",
          hosts: data.events?.[0]?.hosts || "",
          host_surname: data.events?.[0]?.host_surname || "",
          guests: data.events?.[0]?.guests || "",
          guest_surname: data.events?.[0]?.guest_surname || "",
          start_date_of_event: data.events?.[0]?.start_date_of_event || "",
          end_date_of_event: data.events?.[0]?.end_date_of_event || "",
          expected_audience_size: data.events?.[0]?.expected_audience_size,
          enable_livestreaming: data.events?.[0]?.enable_livestreaming,
          record_stream: data.events?.[0]?.record_stream,
          streaming_platform: data.events?.[0]?.streaming_platform || "",
          require_custom_branding: data.events?.[0]?.require_custom_branding,
          retention_duration: data.events?.[0]?.retention_duration || "",
          branding_requirements: data.events?.[0]?.branding_requirements || "",
          session_to_stream: data.events?.[0]?.session_to_stream || "",
          specific_sessions: data.events?.[0]?.specific_sessions || "",
          access_type: data.events?.[0]?.access_type || "",
          provide_on_demand_access: data.events?.[0]?.provide_on_demand_access,

          company_contact_id: data.company_contact?.id,
          company_name:
            data.company_contact?.company_name ||
            data.contact?.company_name ||
            "",
          company_email: data.company_contact?.email || "",
          company_phone: data.company_contact?.phone || "",
          company_address: data.company_contact?.address || "",
          company_city: data.company_contact?.city || "",
          company_country: data.company_contact?.country || "",
          deceased_list:
            data.deceased?.map((d) => ({
              id: d.id,
              deceased_name: d.first_name,
              deceased_surname: d.surname,
              second_name: d.second_name,
              date_of_birth: d.date_of_birth,
              date_of_death: d.date_of_death,
              gender: d.gender,
              deceased_salutation: d.salutation,
              deceasedImageFile: null,
              nickname: d.nickname,
              person_image: d.person_image,
              religion: d.religion,
              denomination: d.denomination,
              relationship_to_deceased: d.relationship_to_deceased,
            })) || [],
          services_list: isConferenceOrEvent ? [] : (servicesList as ServicesListPayload[]),
          events_list: isConferenceOrEvent ? (eventsList as EventListPayload[]) : [],
          church_name: data.services?.[0]?.church_name || "",
          leader_preacher_name: data.services?.[0]?.leader_preacher_name || "",
          leader_preacher_surname:
            data.services?.[0]?.leader_preacher_surname || "",
          preacher_name: data.services?.[0]?.preacher_name || "",
          preacher_surname: data.services?.[0]?.preacher_surname || "",
          type_of_service: data.services?.[0]?.type_of_service || "",
          service_notes: data.services?.[0]?.service_notes || "",
          address_one: venueData?.address_one || "",
          address_two: venueData?.address_two || "",
          suburb: venueData?.suburb || "",
          city: venueData?.city || "",
          date_of_service: data.services?.[0]?.date_of_service || "",
          start_time: eventOrServiceData?.start_time?.slice(0, 5) || "",
          end_time: eventOrServiceData?.end_time?.slice(0, 5) || "",
          mc_name: data.services?.[0]?.mc_name || "",
          eulogist_name: data.services?.[0]?.eulogist_name || "",
          venue_ids: isConferenceOrEvent
            ? data.events
                ?.map((e) => e.venue?.id?.toString())
                .filter(Boolean) || []
            : data.services
                ?.map((s) => s.venue?.id?.toString())
                .filter(Boolean) || [],
          deceased_name: data.deceased?.[0]?.first_name || "",
          second_name: data.deceased?.[0]?.second_name || "",
          deceased_surname: data.deceased?.[0]?.surname || "",
          date_of_birth: data.deceased?.[0]?.date_of_birth || "",
          date_of_death: data.deceased?.[0]?.date_of_death || "",
          gender: data.deceased?.[0]?.gender || "",
          deceased_salutation: data.deceased?.[0]?.salutation || "",
          nickname: data.deceased?.[0]?.nickname || "",
          photo_url: data.deceased?.[0]?.person_image || "",
          religion: data.deceased?.[0]?.religion || "",
          denomination: data.deceased?.[0]?.denomination || "",
          relationship_to_deceased:
            data.deceased?.[0]?.relationship_to_deceased || "",
          deceased_id: data.deceased?.[0]?.id,
          package: data.package_item
            ? {
                id: data.package_item.package,
                name: data.package_item.name,
                price: Number.parseFloat(data.package_item.price),
                contents: [data.package_item.description],
              }
            : undefined,
          package_item: data.package_item
            ? {
                id: data.package_item.id,
                name: data.package_item.name,
                price: Number.parseFloat(data.package_item.price),
                package: data.package_item.package,
                description: data.package_item.description,
              }
            : undefined,
          AVEquipment:
            data.package_item?.services
              ?.filter(
                (s) => s.service_category === "Audio Visual Equipment"
              )
              .map((s) => s.id?.toString()) || [],
          Screens:
            data.package_item?.services
              ?.filter((s) => s.service_category === "Screens")
              .map((s) => s.id?.toString()) || [],
          Design:
            data.package_item?.services
              ?.filter((s) => s.service_category === "Design")
              .map((s) => s.id?.toString()) || [],
          "Visual Presentation & Audio":
            data.package_item?.services
              ?.filter(
                (s) => s.service_category === "Visual Presentation & Audio"
              )
              .map((s) => s.id?.toString()) || [],
        });

        setModalBookingTitle(
          `${data.contact?.first_name || ""} ${data.contact?.last_name || ""}`
        );
        setLoading(false);
      } catch (err: unknown) {
        console.error("Error fetching booking:", err);
        setSubmissionError("Failed to fetch booking.");
        setLoading(false);
      }
    };

    fetchBookingData();
  }, [id]);

  const stepsConfig: Record<string, StepConfig[]> = {
    EVENT: [
      { label: "Client Information" },
      { label: "Event Details" },
      { label: "Package Selection" },
    ],
    CONFERENCE: [
      { label: "Client Information" },
      { label: "Conference Details" },
      { label: "Package Selection" },
    ],
    FUNERAL: [
      { label: "Client Information" },
      { label: "Service Details" },
      { label: "Package Selection" },
    ],
    CHURCH: [
      { label: "Client Information" },
      { label: "Service Details" },
      { label: "Package Selection" },
    ],
    OTHER: [
      { label: "Client Information" },
      { label: "Service Details" },
      { label: "Package Selection" },
    ],
    FUNERAL_HOME: [
      { label: "Client Information" },
      { label: "Service Details" },
      { label: "Package Selection" },
    ],
  };

  const tabs: StepConfig[] =
    stepsConfig[currentServiceType] || stepsConfig.FUNERAL;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSubmitting = async (values: BookingData) => {
    if (!id) {
      toast.error("Booking ID is missing for update.");
      return;
    }

    setSubmitting(true);
    setModalOpen(true);
    setSubmissionError(null);
    setSubmissionSuccess(null);

    try {
      const valuesToSubmit = { ...values };
      const selectedVenueId = valuesToSubmit.venue_ids?.[0]
        ? Number.parseInt(valuesToSubmit.venue_ids[0])
        : valuesToSubmit.events_list?.[0]?.venue_id ??
          valuesToSubmit.services_list?.[0]?.venue_id;
      if (
        currentServiceType === "CONFERENCE" ||
        currentServiceType === "OTHER"
      ) {
        if (valuesToSubmit.events_list && valuesToSubmit.events_list.length > 0) {
          valuesToSubmit.events_list[0] = {
            ...valuesToSubmit.events_list[0],
            title: valuesToSubmit.title ?? null,
            hosts: valuesToSubmit.hosts ?? null,
            host_surname: valuesToSubmit.host_surname ?? null,
            guests: valuesToSubmit.guests ?? null,
            guest_surname: valuesToSubmit.guest_surname ?? null,
            conference_name: valuesToSubmit.conference_name ?? null,
            start_date_of_event: valuesToSubmit.start_date_of_event ?? null,
            end_date_of_event: valuesToSubmit.end_date_of_event ?? null,
            start_time: valuesToSubmit.start_time ?? null,
            end_time: valuesToSubmit.end_time ?? null,
            expected_audience_size: valuesToSubmit.expected_audience_size,
            record_stream: valuesToSubmit.record_stream ?? null,
            streaming_platform: valuesToSubmit.streaming_platform ?? null,
            session_to_stream: valuesToSubmit.session_to_stream ?? null,
            specific_sessions: valuesToSubmit.specific_sessions ?? null,
            access_type: valuesToSubmit.access_type ?? null,
            provide_on_demand_access: valuesToSubmit.provide_on_demand_access ?? null,
            retention_duration: valuesToSubmit.retention_duration ?? null,
            enable_livestreaming: valuesToSubmit.enable_livestreaming ?? null,
            require_custom_branding: valuesToSubmit.require_custom_branding ?? null,
            branding_requirements: valuesToSubmit.branding_requirements ?? null,
            service_notes:
              valuesToSubmit.notes ?? valuesToSubmit.service_notes ?? null,
            special_request: valuesToSubmit.special_request ?? null,
            venue_id: selectedVenueId ?? null,
          };
        }
      } else {
        if (
          valuesToSubmit.services_list &&
          valuesToSubmit.services_list.length > 0
        ) {
          valuesToSubmit.services_list = valuesToSubmit.services_list.map(
            (service: ServicesListPayload) => ({
              ...service,
              date_of_service: valuesToSubmit.date_of_service ?? null,
              start_time: valuesToSubmit.start_time ?? null,
              end_time: valuesToSubmit.end_time ?? null,
              special_request: valuesToSubmit.special_request ?? null,
              service_notes: valuesToSubmit.service_notes ?? null,
              mc_name: valuesToSubmit.mc_name ?? null,
              eulogist_name: valuesToSubmit.eulogist_name ?? null,
              church_name: valuesToSubmit.church_name ?? null,
              leader_preacher_name: valuesToSubmit.leader_preacher_name ?? null,
              leader_preacher_surname:
                valuesToSubmit.leader_preacher_surname ?? null,
              preacher_name: valuesToSubmit.preacher_name ?? null,
              preacher_surname: valuesToSubmit.preacher_surname ?? null,
              type_of_service: valuesToSubmit.type_of_service ?? null,

              venue_id: service.venue_id,
            })
          );
        }

        if (
          valuesToSubmit.deceased_list &&
          valuesToSubmit.deceased_list.length > 0
        ) {
          valuesToSubmit.deceased_list = valuesToSubmit.deceased_list.map(
            (deceased) => ({
              ...deceased,
              relationship_to_deceased:
                valuesToSubmit.relationship_to_deceased ||
                deceased.relationship_to_deceased,
            })
          );
        }
      }

      const formData = await buildBookingFormData(
        valuesToSubmit,
        true,
        currentServiceType
      );
      const bookingId = Number(id);
      if (isNaN(bookingId)) {
        throw new Error("Invalid booking ID");
      }
      formData.append("booking_id", String(bookingId));

      await apiService.put(
        `/api/bookings/booking/update-fullbooking/`,
        formData
      );

      toast.success("Booking updated successfully!", {
        position: "top-right",
        autoClose: 2000,
        theme: "light",
      });

      setTimeout(() => {
        navigate(`/bookings/${id}`);
      }, 2000);
    } catch (err: unknown) {
      const errorResponse = (err as {
        response?: { data?: Record<string, unknown> };
      })?.response;
      const errorMessage =
        (errorResponse?.data as { detail?: string })?.detail ||
        Object.values(errorResponse?.data || {})
          .flat()
          .join(" ") ||
        "Failed to update booking.";
      toast.error(errorMessage);
      console.error("Update error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const getTabContent = (tabIndex: number) => {
    const stepLabel = tabs[tabIndex]?.label;
    switch (stepLabel) {
      case "Client Information":
        return (
          <ClientInformation
            serviceType={currentServiceType}
            bookingData={bookingData}
            setBookingData={setBookingData}
            currentContact={currentContact}
          />
        );
      case "Service Details":
      case "Event Details":
        return (
          <ServiceInformation
            serviceType={currentServiceType}
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        );
      case "Conference Details":
        return (
          <>
            <ConferenceBookingForm
              bookingData={bookingData}
              setBookingData={setBookingData}
              serviceType={currentServiceType}
            />
            <SiteDetails
              bookingData={bookingData}
              setBookingData={setBookingData}
              serviceType={currentServiceType}
            />
          </>
        );

      case "Package Selection":
        return (
          <PackageSelection
            serviceType={currentServiceType}
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        );
      default:
        return <Typography>Unknown tab: {stepLabel}</Typography>;
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress sx={{ color: colors.primary[800] }} />
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        p: 2,
        pt: 3,
        mb: 3,
        borderRadius: 2,
        minHeight: "80vh",
        position: "relative",
        boxShadow: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="edit booking tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab) => (
            <Tab key={tab.label} label={tab.label} />
          ))}
        </Tabs>
        <Button
          variant="contained"
          sx={{ backgroundColor: colors.primary[400], mr: 1 }}
          onClick={() => handleSubmitting(bookingData)}
          disabled={submitting}
        >
          {submitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Update Booking"
          )}
        </Button>
      </Box>

      <Box minHeight={30}></Box>
      <Box sx={{ p: 3 }}>{getTabContent(activeTab)}</Box>
    </Paper>
  );
};

export default EditBooking;
